/**
 * TDS and GST on partner commission.
 *
 * Nothing is built in: no rate, no threshold, no section. A rule is entered by
 * somebody with `partners.tax.manage`, takes effect from a date, and is never
 * edited — a change is a new rule from a later date, so a payout approved last
 * year still names the rule it was taxed under. Until a TDS rule exists, no
 * payout can be approved; a rate of 0 is a decision written down here.
 * The company's tax adviser reviews a rule before the first payout under it.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Input,
  LoadingState,
  PageHeader,
  Select,
  Textarea,
  formatDate,
  formatMoney,
  todayISO,
  toast,
} from '@connect/web-shared';

import { partnerKeys, partnersApi, percent } from './api';
import { Failed, errorText } from './shared';

export function PartnerTaxPage() {
  const queryClient = useQueryClient();
  const rules = useQuery({ queryKey: partnerKeys.tax, queryFn: partnersApi.taxRules });
  const [form, setForm] = useState({
    kind: 'tds',
    rate: '',
    rate_without_pan: '',
    annual_threshold: '',
    section: '',
    effective_from: todayISO(),
    note: '',
  });
  const [reviewed, setReviewed] = useState(false);
  const create = useMutation({
    mutationFn: () =>
      partnersApi.createTaxRule({
        ...form,
        annual_threshold: form.kind === 'tds' && form.annual_threshold ? form.annual_threshold : null,
        rate_without_pan: form.kind === 'tds' && form.rate_without_pan ? form.rate_without_pan : null,
        adviser_reviewed: reviewed,
      }),
    onSuccess: async () => {
      toast.success('Rule added');
      setForm((f) => ({ ...f, rate: '', rate_without_pan: '', annual_threshold: '', note: '' }));
      setReviewed(false);
      await queryClient.invalidateQueries({ queryKey: partnerKeys.tax });
    },
  });
  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: event.target.value }));

  return (
    <div className="space-y-4">
      <PageHeader title="Partner tax" description="TDS deducted from, and GST added to, partner payouts — from a date, never edited." />
      {rules.isPending ? (
        <LoadingState />
      ) : rules.isError ? (
        <Failed error={rules.error} onRetry={() => void rules.refetch()} />
      ) : (
        <>
          {!rules.data.some((r) => r.kind === 'tds') ? (
            <Alert tone="warning" title="No TDS rule yet">
              Payouts cannot be approved until one exists. Enter what the company's tax adviser has confirmed.
            </Alert>
          ) : rules.data.some((r) => !r.adviser_reviewed) ? (
            <Alert tone="info">A rule not marked as reviewed by the tax adviser should be reviewed before payouts under it.</Alert>
          ) : null}
          <Card>
            <CardHeader title="Rules" />
            <CardBody className="divide-y divide-line">
              {rules.data.length === 0 ? <p className="text-sm text-muted">None yet.</p> : null}
              {rules.data.map((r) => (
                <div key={r.public_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <div>
                    <p className="font-medium">
                      {r.kind.toUpperCase()} {percent(r.rate)} {r.section ? `· ${r.section}` : ''}
                    </p>
                    <p className="text-xs text-muted">
                      From {formatDate(r.effective_from)}
                      {r.rate_without_pan ? ` · ${percent(r.rate_without_pan)} without a PAN` : ''}
                      {r.annual_threshold ? ` · once a partner's year passes ${formatMoney(r.annual_threshold)}` : ''}
                      {r.note ? ` · ${r.note}` : ''}
                    </p>
                  </div>
                  <Badge tone={r.adviser_reviewed ? 'success' : 'warning'}>
                    {r.adviser_reviewed ? 'Adviser reviewed' : 'Not reviewed'}
                  </Badge>
                </div>
              ))}
            </CardBody>
          </Card>
          <Can permission="partners.tax.manage">
            <Card>
              <CardHeader title="Add a rule" description="It applies to payouts approved on or after its date." />
              <CardBody className="space-y-3">
                {create.isError ? <Alert tone="danger">{errorText(create.error)}</Alert> : null}
                <div className="grid gap-3 sm:grid-cols-3">
                  <Select
                    label="Tax"
                    value={form.kind}
                    onChange={set('kind')}
                    options={[
                      { value: 'tds', label: 'TDS (deducted)' },
                      { value: 'gst', label: 'GST (added, registered partners)' },
                    ]}
                  />
                  <Input label="Rate %" inputMode="decimal" value={form.rate} onChange={set('rate')} />
                  <Input label="From" type="date" value={form.effective_from} onChange={set('effective_from')} />
                  {form.kind === 'tds' ? (
                    <>
                      <Input label="Section" value={form.section} onChange={set('section')} />
                      <Input
                        label="Rate % without a PAN"
                        hint="For a partner who has given no PAN. Left empty, such a partner cannot be paid."
                        inputMode="decimal"
                        value={form.rate_without_pan}
                        onChange={set('rate_without_pan')}
                      />
                      <Input
                        label="Yearly threshold ₹"
                        hint="Nothing is deducted until a partner's year passes it"
                        inputMode="decimal"
                        value={form.annual_threshold}
                        onChange={set('annual_threshold')}
                      />
                    </>
                  ) : null}
                </div>
                <Textarea label="Note" value={form.note} onChange={set('note')} rows={2} />
                <Checkbox label="The company's tax adviser has reviewed this rule" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />
                <Button loading={create.isPending} disabled={!form.rate || !form.effective_from} onClick={() => create.mutate()}>
                  Add rule
                </Button>
              </CardBody>
            </Card>
          </Can>
        </>
      )}
    </div>
  );
}
